import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  createTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { signup } from "../../redux/apiRequest";

const authFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "var(--toeic-surface)",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name || !email || !password) {
      setErrorMessage("All fields are required.");
      setOpenSnackbar(true);
      return;
    }

    if (rememberMe) {
      Cookies.set("email", email, { expires: 7 });
      Cookies.set("password", password, { expires: 7 });
    } else {
      Cookies.remove("email");
      Cookies.remove("password");
    }

    const user = { name, email, password };
    const result = await signup(user, dispatch, navigate);

    if (result === false) {
      setErrorMessage("Signup failed. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container
      sx={{
        mt: 6,
        mb: 6,
        width: "100%",
        maxWidth: "1120px !important",
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: "18px",
          border: "1px solid rgba(185, 220, 255, 0.72)",
          bgcolor: "var(--toeic-surface)",
          boxShadow: "var(--toeic-shadow-card)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
          minHeight: isMobile ? "auto" : 650,
        }}
      >
        <Box
          sx={{
            p: isMobile ? 3 : 5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ color: "var(--toeic-ink-deep)", fontWeight: 800, fontSize: { xs: 32, md: 44 }, lineHeight: 1.05 }}
          >
            <Box component="span" sx={{ position: "relative", display: "inline-block", "&::before": { content: '""', position: "absolute", left: "-0.12em", right: "-0.12em", bottom: "0.04em", height: "0.44em", bgcolor: "rgba(79, 191, 159, 0.22)", borderRadius: "0.45em 0.6em 0.4em 0.55em", transform: "rotate(-1deg)" } }}>
              <Box component="span" sx={{ position: "relative" }}>Create account</Box>
            </Box>
          </Typography>
          <Typography sx={{ mt: 1.5, mb: 3, color: "var(--toeic-ink-soft)", fontWeight: 700, lineHeight: 1.65 }}>
            Start saving your tests, scores, and review history.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!name && openSnackbar}
              helperText={!name && openSnackbar ? "Name is required" : ""}
              sx={{ ...authFieldSx, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "var(--toeic-ink-soft)" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!email && openSnackbar}
              helperText={!email && openSnackbar ? "Email is required" : ""}
              sx={{ ...authFieldSx, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "var(--toeic-ink-soft)" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!password && openSnackbar}
              helperText={
                !password && openSnackbar ? "Password is required" : ""
              }
              sx={{ ...authFieldSx, mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "var(--toeic-ink-soft)" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                mb: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember me"
              />
              <Typography
                component={Link}
                to="/signin"
                sx={{
                  color: "#2196f3",
                  fontWeight: 800,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Sign in instead
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<WorkspacePremiumIcon />}
              sx={{
                py: 1.3,
                borderRadius: "14px",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#35c759",
                color: "#ffffff",
                "&:hover": { bgcolor: "#2fb44f" },
              }}
            >
              Sign up
            </Button>
          </Box>

          <Typography
            component={Link}
            to="/"
            sx={{
              mt: 3,
              color: "#5f6b7a",
              textAlign: "center",
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            Back to Home
          </Typography>
        </Box>

        <Box
          sx={{
            p: isMobile ? 3 : 5,
            bgcolor: "var(--toeic-paper-grid)",
            borderLeft: isMobile ? "none" : "1.5px dotted var(--toeic-line)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 4,
          }}
        >
          <Box>
            <Chip
              icon={<SchoolIcon />}
              label="Practice smarter"
              sx={{
                mb: 3,
                color: "#2196f3",
                backgroundColor: "#edf7ff",
                fontWeight: 800,
                "& .MuiChip-icon": { color: "#2196f3" },
              }}
            />
            <Typography
              variant={isMobile ? "h4" : "h3"}
              sx={{ color: "var(--toeic-ink-deep)", fontWeight: 800, lineHeight: 1.08 }}
            >
              Build your score history from the first attempt.
            </Typography>
            <Typography sx={{ mt: 2, color: "var(--toeic-ink-soft)", fontWeight: 700, lineHeight: 1.65 }}>
              Save answer sheets, compare your best trials, and review mistakes
              whenever you want.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: "14px",
              backgroundColor: "var(--toeic-surface)",
              border: 0,
              boxShadow: "var(--toeic-shadow-card)",
            }}
          >
            <CheckCircleIcon sx={{ mb: 1, fontSize: 34, color: "#35c759" }} />
            <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700 }}>Score growth</Typography>
            <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 700 }}>
              Every completed test becomes part of your personal leaderboard.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%", borderRadius: "14px", fontWeight: 800, boxShadow: "var(--toeic-shadow-card)" }}
        >
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
}

export default Signup;

